import { Bookmark, BookmarkBorder, Help } from "@mui/icons-material";
import { LinearProgress, Box, Icon, Stack } from "@mui/material";
import Button from "components/Button";
import Card from "components/Cards/Card";
import ChipsPreview from "components/UI/atoms/ChipsPreview";
import MainTabs from "components/UI/organisms/MainTabs";
import { useLocalizedTranslation } from "helpers/hooks/useLocalizedTranslation";
import { COMPONENT_TYPES } from "helpers/utils/constants";
import { isTrue } from "helpers/utils/validators";
import AssociateChallengeListing from "pages/associateListings/AssociateChallengeListing";
import AssociateLabListing from "pages/associateListings/AssociateLabListing";
import AssociateResourceListing from "pages/associateListings/AssociateResourceListing";
import MainPageLayout from "pages/layouts/MainPage";
import DashboardLayout from "pages/layouts/dashboard";
import { AssociatedItem } from "pages/projects/components/ProjectDescription";
import ImageCard from "components/Cards/ImageCard";
import React, { useState } from "react";
import useRedirect from "helpers/hooks/useRedirect";
import useCareers from "store/careers/service-hook";
import { payloadBool } from "helpers/utils/utilities";
import ShareModal from "components/UI/organisms/ShareModal";
import ReadMore from "components/UI/atoms/ReadMore";
import { useSelector } from "react-redux";
import { REDUCER_TYPES } from "store";
import { cachedLoginNavigate } from "store/api/utils";

const TABS = ["Related Challenges", "Related Labs", "Related Resources"];

const DetailPageLayout = ({
  loading,
  data,
  setData = Function.prototype,
  isSkill,
}) => {
  const [tab, setTab] = useState(0);
  const { save, unsave } = useCareers();
  const { t } = useLocalizedTranslation();
  const [shareOpen, setShareOpen] = useState(false);
  const { navigateChallenges, navigateLabs, navigateResources } = useRedirect();
  const { isLoggedIn } = useSelector((state) => state[REDUCER_TYPES.AUTH]);

  const handleSave = async () => {
    const alreadySaved = isTrue(data?.is_saved);
    alreadySaved
      ? await unsave(data?.id, isSkill)
      : await save(data?.id, isSkill);
    setData((prev) => ({
      ...prev,
      is_saved: payloadBool(!alreadySaved),
    }));
  };

  return (
    <DashboardLayout loading={loading} isEmpty={!data}>
      <MainPageLayout
        classes="mt-header pb-6"
        firstLgGridSize={4.5}
        secondLgGridSize={7.5}
      >
        <div className="left">
          <ImageCard
            height="250px"
            classes="mt-4"
            type={isSkill ? COMPONENT_TYPES.SKILL : COMPONENT_TYPES.CAREER}
            title={data?.title}
          />
          <Card classes="mt-3">
            <h2 className="c-primary">{data?.title}</h2>
            <Stack direction="row" gap={1} mt={2}>
              <Button
                classes="fs-13"
                bordered
                height="30px"
                onClick={handleSave}
              >
                <Icon
                  component={isTrue(data?.is_saved) ? BookmarkBorder : Bookmark}
                  className="c-primary"
                  fontSize="small"
                />
                {isTrue(data?.is_saved) ? t("Unsave") : t("Save")}
              </Button>
              {!isSkill && (
                <Button
                  bordered
                  classes="fs-13"
                  height="30px"
                  onClick={() =>
                    !isLoggedIn ? cachedLoginNavigate() : setShareOpen(true)
                  }
                >
                  {t("Share", "common")}
                </Button>
              )}
            </Stack>
            <ReadMore classes="fs-12 mt-4" text={data?.description} />
            {isSkill && data?.skill_url && (
              <p
                className="link fs-12 mt-1"
                onClick={() => window.open(data?.skill_url)}
              >
                {t("Read More on Wikipedia")}
              </p>
            )}
            {data?.related_skills?.length ? (
              <>
                <h3 className="mt-4">{t("Related Skills")}</h3>
                <ChipsPreview items={data?.related_skills} />
              </>
            ) : null}

            {!isSkill && (
              <Box className="px-2 py-2 my-4 bg-primary--opaque rounded-5">
                <h3 className="mr-2 flex items-center">
                  {t("Your Skill Match")} 
                  {/* <Help className="ml-1" /> */}
                </h3>
                <div className="flex items-center mt-1">
                  <LinearProgress
                    variant="determinate"
                    value={
                      data?.skills?.length > 0 && data?.skills_percentage
                        ? data?.skills_percentage
                        : 0
                    }
                    sx={{
                      width: "80%",
                      height: "8px",
                      borderRadius: "50px",
                      mr: 1,
                    }}
                  />
                  <p className="fs-12">
                    {data?.skills?.length > 0 && data?.skills_percentage
                      ? data?.skills_percentage
                      : 0}
                    %
                  </p>
                </div>
                <p className="fs-12 mt-3">
                  {t(
                    data?.skills?.length > 0 && data?.skills_percentage
                      ? `Verify following Skills on PreprLabs to increase your skill match percentage.`
                      : `Start joining Challenges and Labs to verify job related Skills to increase your skill match percentage.`
                  )}
                </p>
                <div className="mt-2">
                  <ChipsPreview items={data?.skills} />
                </div>
              </Box>
            )}
            {data?.related_careers?.length ? (
              <>
                <h3 className="mt-4">{t("Related Careers")}</h3>
                {data?.related_careers?.map((d) => (
                  <AssociatedItem {...d} t={t} type={COMPONENT_TYPES.CAREER} />
                ))}
              </>
            ) : null}
          </Card>
        </div>
        <div className="right mt-4">
          <MainTabs current={tab} setCurrent={setTab} tabs={TABS} />
          {tab === 0 && (
            <AssociateChallengeListing
              parent={isSkill ? COMPONENT_TYPES.SKILL : COMPONENT_TYPES.CAREER}
              slug={data?.id}
              noPagination
              limit={3}
              emptyView={
                <Card classes="w-full ml-4">
                  <h3>{t("Related Challenges")}</h3>
                  <p className="c-gray fw-500 text-center my-8 mx-auto w-70p opacity-80 ">
                    {t("detailPageEmptyAssociationsMessage", "careers", {
                      childComponent: t("Challenge", "common"),
                      component: t(isSkill ? "Skill" : "Career", "common"),
                    })}
                  </p>
                  <div className="flex justify-center mb-4">
                    <Button onClick={navigateChallenges} bordered>
                      {t("Explore Challenges")}
                    </Button>
                  </div>
                </Card>
              }
            >
              <div className="flex justify-center">
                <Button onClick={navigateChallenges} bordered>
                  {t("Explore More Challenges")}
                </Button>
              </div>
            </AssociateChallengeListing>
          )}
          {tab === 1 && (
            <AssociateLabListing
              parent={isSkill ? COMPONENT_TYPES.SKILL : COMPONENT_TYPES.CAREER}
              slug={data?.id}
              noPagination
              limit={3}
              emptyView={
                <Card classes="w-full ml-4">
                  <h3>{t("Related Labs")}</h3>
                  <p className="c-gray fw-500 text-center my-8 mx-auto w-70p opacity-80 ">
                    {t("detailPageEmptyAssociationsMessage", "careers", {
                      childComponent: t("Lab", "common"),
                      component: t(isSkill ? "Skill" : "Career", "common"),
                    })}
                  </p>
                  <div className="flex justify-center mb-4">
                    <Button onClick={navigateLabs} bordered>
                      {t("Explore Labs")}
                    </Button>
                  </div>
                </Card>
              }
            >
              <div className="flex justify-center">
                <Button onClick={navigateLabs} bordered>
                  {t("Explore More Labs")}
                </Button>
              </div>
            </AssociateLabListing>
          )}
          {tab === 2 && (
            <AssociateResourceListing
              parent={isSkill ? COMPONENT_TYPES.SKILL : COMPONENT_TYPES.CAREER}
              slug={data?.id}
              noPagination
              limit={3}
              emptyView={
                <Card classes="w-full ml-4">
                  <h3>{t("Related Resources")}</h3>
                  <p className="c-gray fw-500 text-center my-8 mx-auto w-70p opacity-80 ">
                    {t("detailPageEmptyAssociationsMessage", "careers", {
                      childComponent: t("Resource", "common"),
                      component: t(isSkill ? "Skill" : "Career", "common"),
                    })}
                  </p>
                  <div className="flex justify-center mb-4">
                    <Button onClick={navigateResources} bordered>
                      {t("Explore Resources")}
                    </Button>
                  </div>
                </Card>
              }
            >
              <div className="flex justify-center">
                <Button onClick={navigateResources} bordered>
                  {t("Explore More Resources")}
                </Button>
              </div>
            </AssociateResourceListing>
          )}
        </div>
      </MainPageLayout>
      {shareOpen && (
        <ShareModal
          open={shareOpen}
          onClose={() => setShareOpen(false)}
          component={COMPONENT_TYPES.CAREER}
          slug={data?.id}
        />
      )}
    </DashboardLayout>
  );
};

export default DetailPageLayout;
